import React, { useEffect, useMemo, useState } from 'react'
import { debounce, isEmpty, flatMap, uniq } from 'lodash'
import { isAdmin, isManager, useSession } from '@slerp/accounts'
import { useQuery, useLazyQuery } from '@apollo/client'
import { useMutation } from '@apollo/client'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Menu from 'antd/lib/menu'
import Input from 'antd/lib/input'
import Button from 'antd/lib/button'
import Select from 'antd/lib/select'
import message from 'antd/lib/message'
import Dropdown from 'antd/lib/dropdown'
import { EditOutlined } from '@ant-design/icons'
import InventoryPublishDrawer from '../InventoryPublishDrawer'
import PricingDrawer from '../Pricings/Drawer'
import AvailabilityGroupModal from './AvailabilityGroupModal'
import VariantsInventoryTable from './VariantsInventoryTable'
import ModifiersInventoryTable from './ModifiersInventoryTable'
import { useAvailabilityGroupModal } from './useAvailabilityGroupModal'
import {
  CREATE_STORE_VARIANTS,
  PUBLISH_STORE_VARIANTS,
  CREATE_STORE_MODIFIERS,
  PUBLISH_STORE_MODIFIERS,
  UNPUBLISH_STORE_VARIANTS,
  UNPUBLISH_STORE_MODIFIERS,
  GET_STORE_SAMEDAY_INVENTORY,
  GET_MERCHANT_MODIFIER_GROUPS,
  GET_STORE_PREORDER_INVENTORY,
  GET_CUT_OFF_RULES_BY_STORE_ID,
  PUBLISH_PRE_ORDER_STORE_VARIANTS,
  PUBLISH_PRE_ORDER_STORE_MODIFIERS,
  UNPUBLISH_PRE_ORDER_STORE_VARIANTS,
  UPDATE_SAMEDAY_VARIANT_STOCK_COUNT,
  UNPUBLISH_PRE_ORDER_STORE_MODIFIERS,
  UPDATE_PREORDER_VARIANT_STOCK_COUNT,
  UPDATE_STORE_CATEGORIES_ARRANGEMENTS,
  UPDATE_STORE_MODIFIER_GROUP_ARRANGEMENTS,
  BULK_UPDATE_AVAILABILITY
} from '../DashboardQueries'
import { SelectPrefixWrapper } from '../../../Widgets'
import { GET_PRODUCTS_CATEGORIES_BY_CATEGORY_IDS } from 'components/Locations/Dashboard/DashboardQueries'
import { ProductCategory } from 'components/ProductsAndModifiers/Products/Form'
import { AppsIcon, FilterIcon, SelectDownArrowIcon } from '@slerp/assets'
import {
  StoreVariant,
  StoreModifier,
  ModifierGroup,
  StoreInventory,
  InventoriesProps,
  StoreVariantMultipleCategories
} from '../types'

import { Status } from '../../AvailabilityRules/types'
import { AvailabilityAction, ItemType } from './types'
import { generateSuccessMessage, generateErrorMessage } from './utils'

const { Search } = Input

const Inventories = (props: InventoriesProps) => {
  const {
    storeInfo,
    isPreorder,
    categories,
    categoryIds,
    isLoadingCategories,
    isInventoryDrawerOpen,
    inventoryDrawerTab,
    navigate,
    slug
  } = props
  const [storeVariants, setStoreVariants] = useState<Array<StoreVariant>>([])
  const { merchant, user } = useSession()
  const [storeModifiers, setStoreModifiers] = useState<Array<StoreModifier>>([])
  const [inventoryProfile, setInventoryProfile] = useState('variants')
  const [processingVariantStockToggle, setProcessingVariantStockToggle] =
    useState<Array<string>>([])
  const [isVariantTogglingStockState, setIsVariantTogglingStockState] =
    useState(false)
  const [processingModifierStockToggle, setProcessingModifierStockToggle] =
    useState<Array<string>>([])
  const [isModifierTogglingStockState, setIsModifierTogglingStockState] =
    useState(false)
  const [categoryIdFilter, setCategoryIdFilter] = useState('all')
  const [modifierGroupIdFilter, setModifierGroupIdFilter] = useState('all')
  const [selectedVariantsRowKeys, setSelectedVariantsRowKeys] = useState<
    string[]
  >([])
  const [selectedModifiersRowKeys, setSelectedModifiersRowKeys] = useState<
    string[]
  >([])
  const [fuzzySearchKeyword, setFuzzySearchKeyword] = useState('')
  const [variantStockCountLoading, setVariantStockCountLoading] = useState<
    Array<string>
  >([])
  const [_modifierStockCountLoading, setModifierStockCountLoading] = useState<
    Array<string>
  >([])
  const [selectedStoreVariant, setSelectedStoreVariant] =
    useState<StoreVariant | null>(null)
  const isPricingDrawerVisible = selectedStoreVariant != null
  const [modifiedStoreInventory, setModifiedStoreInventory] =
    useState<StoreInventory>()
  const [isLoadingPristineInventory, setIsLoadingPristineInventory] =
    useState<boolean>(true)

  const canManage = isAdmin(user) || isManager(user)

  const { data: modifierGroupsData } = useQuery(GET_MERCHANT_MODIFIER_GROUPS, {
    variables: { merchantId: merchant?.id },
    fetchPolicy: 'no-cache'
  })
  const modifierGroups: ModifierGroup[] =
    modifierGroupsData?.modifier_groups ?? []

  const [
    getProductCategories,
    { loading: isLoadingProductsCategories, refetch: refetchProductsCategories }
  ] = useLazyQuery<{ products_categories: ProductCategory[] }>(
    GET_PRODUCTS_CATEGORIES_BY_CATEGORY_IDS,
    {
      variables: {
        categoryIds: props.mockCategoryIdsParam ?? categoryIds
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data: { products_categories: ProductCategory[] }) => {
        if (!storeInventoryData) return
        const { store_variants, store_modifiers } = storeInventoryData
        const { products_categories } = data

        const variantsWithCategories = store_variants.map(
          (sv: StoreVariant) => {
            const involvedCategories = products_categories.filter(
              (pc: ProductCategory) =>
                pc.product_id === sv.product_variant.product.id
            )

            return {
              ...sv,
              categories: involvedCategories
            }
          }
        )

        setModifiedStoreInventory({
          store_modifiers: store_modifiers,
          store_variants: variantsWithCategories
        })
      }
    }
  )

  const [
    getStoreInventory,
    {
      data: storeInventoryData,
      refetch: refetchStoreInventory,
      loading: isLoadingStoreInventory
    }
  ] = useLazyQuery<StoreInventory>(
    isPreorder ? GET_STORE_PREORDER_INVENTORY : GET_STORE_SAMEDAY_INVENTORY,
    {
      variables: { storeId: storeInfo.id },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: (storeInventoryData) => {
        if (!storeInventoryData) return

        getProductCategories()
        const { store_modifiers } = storeInventoryData

        const filtered =
          modifierGroupIdFilter === 'all'
            ? store_modifiers
            : store_modifiers.filter((sm: StoreModifier) =>
                sm.modifier.modifier_group_modifiers.some(
                  (mgm) => mgm.modifier_group_id === modifierGroupIdFilter
                )
              )

        setStoreModifiers(filtered.filter(modifiersFuzzySearchHandler))
      }
    }
  )

  useEffect(() => {
    if (storeInfo.id) {
      getStoreInventory()
    }
  }, [storeInfo.id])

  const { data: cutOffRulesData, loading: isLoadingCutOffRules } = useQuery<{
    getCutOffRulesByStoreId: {
      status: Status
      categories: {
        id: string
      }[]
    }[]
  }>(GET_CUT_OFF_RULES_BY_STORE_ID, {
    variables: {
      storeId: storeInfo.id
    },
    fetchPolicy: 'no-cache',
    skip: !storeInfo.id || isLoadingProductsCategories
  })

  // filter out inactive/draft rules
  // flattens map to only be a list of category ids
  const categoriesWithCutOffRules = uniq(
    flatMap(
      cutOffRulesData?.getCutOffRulesByStoreId
        .filter((rule) => rule.status === Status.active)
        .map((rule) => rule.categories.map((c) => c.id))
    )
  )

  const [unPublishStoreVariants] = useMutation(UNPUBLISH_STORE_VARIANTS, {
    fetchPolicy: 'no-cache'
  })
  const [publishStoreVariants] = useMutation(PUBLISH_STORE_VARIANTS, {
    fetchPolicy: 'no-cache'
  })
  const [unPublishPreOrderStoreVariants] = useMutation(
    UNPUBLISH_PRE_ORDER_STORE_VARIANTS,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [publishPreOrderStoreVariants] = useMutation(
    PUBLISH_PRE_ORDER_STORE_VARIANTS,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [unPublishStoreModifiers] = useMutation(UNPUBLISH_STORE_MODIFIERS, {
    fetchPolicy: 'no-cache'
  })
  const [publishStoreModifiers] = useMutation(PUBLISH_STORE_MODIFIERS, {
    fetchPolicy: 'no-cache'
  })
  const [unPublishPreOrderStoreModifiers] = useMutation(
    UNPUBLISH_PRE_ORDER_STORE_MODIFIERS,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [publishPreOrderStoreModifiers] = useMutation(
    PUBLISH_PRE_ORDER_STORE_MODIFIERS,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [createStoreModifiers] = useMutation(CREATE_STORE_MODIFIERS, {
    fetchPolicy: 'no-cache'
  })
  const [createStoreVariants] = useMutation(CREATE_STORE_VARIANTS, {
    fetchPolicy: 'no-cache'
  })
  const [bulkUpdateAvailability] = useMutation(BULK_UPDATE_AVAILABILITY, {
    fetchPolicy: 'no-cache'
  })

  const [isSnoozingVariant, setIsSnoozingVariant] = useState(false)
  const [isSnoozingModifier, setIsSnoozingModifier] = useState(false)

  const [updateVariantSamedayStockCount] = useMutation(
    UPDATE_SAMEDAY_VARIANT_STOCK_COUNT,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const [updateVariantPreorderStockCount] = useMutation(
    UPDATE_PREORDER_VARIANT_STOCK_COUNT,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const [updateStoreCategoriesArrangements] = useMutation(
    UPDATE_STORE_CATEGORIES_ARRANGEMENTS
  )

  const [updateStoreModifierGroupArrangements] = useMutation(
    UPDATE_STORE_MODIFIER_GROUP_ARRANGEMENTS
  )

  const resetFormState = () => {
    setIsVariantTogglingStockState(false)
    setProcessingVariantStockToggle([])
    setVariantStockCountLoading([])
  }

  const variantsFuzzySearchHandler = (variant: StoreVariant) => {
    const {
      product_variant: { sku = '', name }
    } = variant
    const parsedFilter = fuzzySearchKeyword.toLowerCase()
    return (
      name.toLowerCase().includes(parsedFilter) ||
      (sku || '').toLowerCase().includes(parsedFilter)
    )
  }

  const modifiersFuzzySearchHandler = (modifier: StoreModifier) => {
    const {
      modifier: { sku = '', name }
    } = modifier
    const parsedFilter = fuzzySearchKeyword.toLowerCase()
    return (
      name.toLowerCase().includes(parsedFilter) ||
      (sku || '').toLowerCase().includes(parsedFilter)
    )
  }

  useEffect(() => {
    if (!modifiedStoreInventory) {
      setIsLoadingPristineInventory(false)
      return
    }

    const { store_modifiers, store_variants } = modifiedStoreInventory

    const variants = store_variants.filter(
      (sv: StoreVariant) => sv.product_variant !== null
    )
    const modifiers = store_modifiers.filter(
      (sm: StoreModifier) => sm.modifier !== null
    )

    resetFormState()

    const filteredVariants =
      categoryIdFilter === 'all'
        ? variants
        : variants.filter((sv: StoreVariantMultipleCategories) =>
            sv?.categories?.some(
              (c: Partial<ProductCategory>) =>
                c.category_id === categoryIdFilter
            )
          )
    setStoreVariants(filteredVariants.filter(variantsFuzzySearchHandler))

    const filteredModifiers =
      modifierGroupIdFilter === 'all'
        ? modifiers
        : modifiers.filter((sm: StoreModifier) =>
            sm.modifier.modifier_group_modifiers.some(
              (mgm) => mgm.modifier_group_id === modifierGroupIdFilter
            )
          )
    setStoreModifiers(filteredModifiers.filter(modifiersFuzzySearchHandler))

    setIsLoadingPristineInventory(false)
  }, [
    modifiedStoreInventory,
    categoryIdFilter,
    modifierGroupIdFilter,
    fuzzySearchKeyword
  ])

  const togglePublishStatusForVariants = (ids: string[], value: boolean) => {
    const mutation = isPreorder
      ? value
        ? publishPreOrderStoreVariants
        : unPublishPreOrderStoreVariants
      : value
      ? publishStoreVariants
      : unPublishStoreVariants

    mutation({ variables: { variantIds: ids, storeId: storeInfo.id } })
      .then(() => refetchStoreInventory())
      .then(() => refetchProductsCategories())
      .catch(() => {
        message.destroy()
        message.error('Changes cannot be made at this time.', 3)
      })
  }

  const executeBulkAction = async (
    itemType: ItemType,
    ids: string[],
    name: string,
    action: AvailabilityAction,
    updateEntireAvailabilityGroup = false
  ) => {
    const isProduct = itemType === ItemType.PRODUCT
    const isStock =
      action === AvailabilityAction.IN_STOCK ||
      action === AvailabilityAction.OUT_OF_STOCK

    // Resolve store-level IDs for snooze loading indicators
    const storeItemIds = isStock
      ? []
      : isProduct
      ? storeVariants
          .filter((sv) => ids.includes(sv.variant_id))
          .map((sv) => sv.id)
      : storeModifiers
          .filter((sm) => ids.includes(sm.modifier_id))
          .map((sm) => sm.id)

    // Set loading state
    if (isStock && isProduct) {
      setIsVariantTogglingStockState(true)
      setProcessingVariantStockToggle(ids)
    } else if (isStock) {
      setIsModifierTogglingStockState(true)
      setProcessingModifierStockToggle(ids)
    } else if (isProduct) {
      setIsSnoozingVariant(true)
      setVariantStockCountLoading((prev) => [...prev, ...storeItemIds])
    } else {
      setIsSnoozingModifier(true)
      setModifierStockCountLoading((prev) => [...prev, ...storeItemIds])
    }

    try {
      const result = await bulkUpdateAvailability({
        variables: {
          productIds: isProduct ? ids : null,
          modifierIds: isProduct ? null : ids,
          action,
          storeId: storeInfo.id,
          orderType: isPreorder ? 'PREORDER' : 'SAMEDAY',
          updateEntireAvailabilityGroup
        }
      })

      message.destroy()

      if (!isStock) {
        message.success(generateSuccessMessage(name, action), 3)
        await refetchStoreInventory()

        if (isProduct) {
          await refetchProductsCategories()
        }

        return
      }

      const returned = result.data.bulkUpdateAvailability
      const returnedIds = returned.map(
        (r: { storeVariantId: string; storeModifierId: string }) =>
          isProduct ? r.storeVariantId : r.storeModifierId
      )
      const inStock = action === AvailabilityAction.IN_STOCK

      message.success(generateSuccessMessage(name, action), 3)

      if (isProduct) {
        setProcessingVariantStockToggle(returnedIds)
        setIsVariantTogglingStockState(false)
        setStoreVariants((prev) =>
          prev.map((sv) =>
            returnedIds.includes(sv.id) ? { ...sv, in_stock: inStock } : sv
          )
        )
        return
      }

      setStoreModifiers((prev) =>
        prev.map((sm) =>
          returnedIds.includes(sm.id) ? { ...sm, in_stock: inStock } : sm
        )
      )
    } catch (error: any) {
      if (!isStock) {
        const setLoading = isProduct
          ? setVariantStockCountLoading
          : setModifierStockCountLoading
        setLoading((prev) => prev.filter((val) => !storeItemIds.includes(val)))
      }
      message.destroy()
      message.error(generateErrorMessage(action, error), 3)
    } finally {
      if (isStock && !isProduct) {
        setIsModifierTogglingStockState(false)
        setProcessingModifierStockToggle([])
      } else if (!isStock && isProduct) {
        setIsSnoozingVariant(false)
      } else if (!isStock) {
        setIsSnoozingModifier(false)
      }
    }
  }

  const togglePublishStatusForModifiers = (ids: string[], value: boolean) => {
    const mutation = isPreorder
      ? value
        ? publishPreOrderStoreModifiers
        : unPublishPreOrderStoreModifiers
      : value
      ? publishStoreModifiers
      : unPublishStoreModifiers

    mutation({ variables: { modifierIds: ids, storeId: storeInfo.id } })
      .then(() => refetchStoreInventory())
      .then(() => refetchProductsCategories())
      .catch(() => {
        message.destroy()
        message.error('Changes cannot be made at this time.', 3)
      })
  }

  const {
    visible: availabilityGroupModalVisible,
    pendingAction,
    handleDismiss,
    handleConfirm,
    toggleState
  } = useAvailabilityGroupModal({
    storeVariants,
    storeModifiers,
    executeBulkAction
  })

  const storeVariantsActions = (action: string) => {
    const storeVariantsId = storeVariants
      .filter((sv: StoreVariant) => selectedVariantsRowKeys.includes(sv.id))
      .map((sv) => sv.product_variant.product.id)

    switch (action) {
      case 'markInStock':
        executeBulkAction(
          ItemType.PRODUCT,
          storeVariantsId,
          'Selection',
          AvailabilityAction.IN_STOCK
        )
        break
      case 'markOutStock':
        executeBulkAction(
          ItemType.PRODUCT,
          storeVariantsId,
          'Selection',
          AvailabilityAction.OUT_OF_STOCK
        )
        break
      case 'publish':
        togglePublishStatusForVariants(storeVariantsId, true)
        break
      case 'unpublish':
        togglePublishStatusForVariants(storeVariantsId, false)
        break
    }
  }

  const storeModifiersActions = (action: string) => {
    const storeModifiersId = storeModifiers
      .filter((sv: StoreModifier) => selectedModifiersRowKeys.includes(sv.id))
      .map((sv) => sv.modifier_id)

    switch (action) {
      case 'publish':
        togglePublishStatusForModifiers(storeModifiersId, true)
        break
      case 'unpublish':
        togglePublishStatusForModifiers(storeModifiersId, false)
        break
    }
  }

  const handleActionMenuProcedure = (event: any) => {
    const { key } = event
    if (inventoryProfile === 'variants') {
      storeVariantsActions(key)
    } else {
      storeModifiersActions(key)
    }
  }

  const actionMenus = () => {
    return (
      <Menu onClick={handleActionMenuProcedure}>
        {inventoryProfile === 'variants' && (
          <Menu.Item key='markInStock'>Mark In-Stock</Menu.Item>
        )}
        {inventoryProfile === 'variants' && (
          <Menu.Item key='markOutStock'>Mark Out-Stock</Menu.Item>
        )}
        <Menu.Item key='publish'>Publish</Menu.Item>
        <Menu.Item key='unpublish'>Unpublish</Menu.Item>
      </Menu>
    )
  }

  const debouncedFuzzySearchKeyword = useMemo(
    () => debounce(setFuzzySearchKeyword, 200),
    []
  )
  const isActionMenuDisabled = () => {
    if (inventoryProfile === 'variants')
      return selectedVariantsRowKeys.length === 0
    return selectedModifiersRowKeys.length === 0
  }

  const submitStockCountUpdate = async (
    fromStockType: boolean,
    id: string,
    name: string,
    params: {
      variantId: string
      stockType: string | null
      stockCount: number
      stockSold: number
      stockResetCount: number
    }
  ) => {
    setVariantStockCountLoading((prev) => [...prev, id])
    params.stockCount = parseInt(`${params.stockCount || '0'}`)
    params.stockType = isEmpty(params.stockType) ? null : params.stockType
    const mutation = isPreorder
      ? updateVariantPreorderStockCount
      : updateVariantSamedayStockCount

    try {
      const result = await mutation({
        variables: { ...params, storeId: storeInfo.id }
      })

      const stockTypeLabel = isEmpty(params.stockType)
        ? 'disabled'
        : params.stockType?.includes('weekly_')
        ? `set to Weekly (${
            params.stockType.split('_')[1].charAt(0).toUpperCase() +
            params.stockType.split('_')[1].slice(1)
          })`
        : `set to ${
            params.stockType!.charAt(0).toUpperCase() +
            params.stockType!.slice(1)
          }`
      const successMsg = fromStockType
        ? `Reset is ${stockTypeLabel} for ${name}`
        : `Default reset amount updated to ${params.stockCount} for ${name}`
      message.destroy()
      message.success(successMsg)

      const {
        id: returnedId,
        stock_count,
        stock_type,
        stock_reset_count
      } = result.data.update_store_variants.returning[0]

      setVariantStockCountLoading((prev) =>
        prev.filter((vId) => vId !== returnedId)
      )
      setStoreVariants((prev: StoreVariant[]) =>
        prev.map((sv) =>
          sv.id === returnedId
            ? { ...sv, stock_type, stock_count, stock_reset_count }
            : sv
        )
      )
    } catch {
      setVariantStockCountLoading((prev) =>
        prev.filter((val: string) => val !== id)
      )
      message.destroy()
      message.error('Changes cannot be made at this time.', 3)
    }
  }

  return (
    <>
      <Row align='middle' justify='space-between'>
        <Col>
          <Row>
            <Col className='_ml-4'>
              <SelectPrefixWrapper
                prefixicon={<FilterIcon />}
                style={{ marginRight: '24px' }}
              >
                <Select
                  style={{ width: '100%' }}
                  defaultValue={inventoryProfile}
                  onChange={setInventoryProfile}
                  suffixIcon={<SelectDownArrowIcon />}
                  data-testid='inventory-dashboard-inventory-type-select'
                >
                  <Select.Option key='variants' value='variants'>
                    Products
                  </Select.Option>
                  <Select.Option key='modifiers' value='modifiers'>
                    Modifiers
                  </Select.Option>
                </Select>
              </SelectPrefixWrapper>
            </Col>
            <Col>
              {inventoryProfile === 'variants' && (
                <SelectPrefixWrapper prefixicon={<AppsIcon />}>
                  <Select
                    suffixIcon={<SelectDownArrowIcon />}
                    className='_w-100 _mr-24'
                    defaultValue='all'
                    onChange={setCategoryIdFilter}
                    data-testid='inventory-dashboard-category-select'
                    loading={isLoadingCategories}
                  >
                    <Select.Option key='all' value='all'>
                      All Categories
                    </Select.Option>
                    {categories.map((category) => (
                      <Select.Option key={category.id} value={category.id}>
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </SelectPrefixWrapper>
              )}
              {inventoryProfile === 'modifiers' && (
                <SelectPrefixWrapper prefixicon={<AppsIcon />}>
                  <Select
                    suffixIcon={<SelectDownArrowIcon />}
                    className='_w-100 _mr-24'
                    defaultValue='all'
                    onChange={setModifierGroupIdFilter}
                    data-testid='inventory-dashboard-modifier-group-select'
                  >
                    <Select.Option key='all' value='all'>
                      All Modifier Groups
                    </Select.Option>
                    {modifierGroups.map((modifierGroup) => (
                      <Select.Option
                        key={modifierGroup.id}
                        value={modifierGroup.id}
                      >
                        {modifierGroup.name}
                      </Select.Option>
                    ))}
                  </Select>
                </SelectPrefixWrapper>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Row justify='end' gutter={8} align='middle'>
            <Col>
              <Search
                placeholder='Search'
                onChange={(e) => debouncedFuzzySearchKeyword(e.target.value)}
                data-testid='inventory-dashboard-search-input'
              />
            </Col>
            {canManage && (
              <>
                <Col>
                  <Dropdown.Button
                    overlay={actionMenus}
                    disabled={isActionMenuDisabled()}
                    data-testid='inventory-dashboard-actions-dd'
                  >
                    Actions
                  </Dropdown.Button>
                </Col>
                <Col>
                  <Button
                    icon={<EditOutlined style={{ verticalAlign: '0.125em' }} />}
                    onClick={() => {
                      const baseUrl = isPreorder
                        ? `/location/${slug}/inventory/pre-orders/edit/products`
                        : `/location/${slug}/inventory/same-day/edit/products`
                      navigate(baseUrl)
                    }}
                    data-testid='inventory-dashboard-edit-inventory-btn'
                  >
                    Edit Inventory
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>
      <Row justify='center'>
        <Col span={24}>
          {inventoryProfile === 'variants' && (
            <VariantsInventoryTable
              storeVariants={storeVariants}
              storeInfo={storeInfo}
              isPreorder={isPreorder}
              isLoading={
                isLoadingCategories ||
                isLoadingStoreInventory ||
                isLoadingProductsCategories ||
                isLoadingPristineInventory ||
                isLoadingCutOffRules
              }
              onToggle={toggleState(ItemType.PRODUCT)}
              onStockCountUpdate={submitStockCountUpdate}
              onSelectVariant={setSelectedStoreVariant}
              processingVariantStockToggle={processingVariantStockToggle}
              isVariantTogglingStockState={isVariantTogglingStockState}
              variantStockCountLoading={variantStockCountLoading}
              snoozingStoreVariant={isSnoozingVariant}
              unsnoozingStoreVariant={isSnoozingVariant}
              categoriesWithCutOffRules={categoriesWithCutOffRules}
              selectedRowKeys={selectedVariantsRowKeys}
              onSelectedRowKeysChange={setSelectedVariantsRowKeys}
            />
          )}
          {inventoryProfile === 'modifiers' && (
            <ModifiersInventoryTable
              storeModifiers={storeModifiers}
              isPreorder={isPreorder}
              isLoading={isLoadingStoreInventory || isLoadingPristineInventory}
              onToggle={toggleState(ItemType.MODIFIER)}
              processingModifierStockToggle={processingModifierStockToggle}
              isModifierTogglingStockState={isModifierTogglingStockState}
              snoozingStoreModifier={isSnoozingModifier}
              unsnoozingStoreModifier={isSnoozingModifier}
              selectedRowKeys={selectedModifiersRowKeys}
              onSelectedRowKeysChange={setSelectedModifiersRowKeys}
            />
          )}
        </Col>
      </Row>
      {selectedStoreVariant && canManage && (
        <PricingDrawer
          visible={isPricingDrawerVisible}
          onClose={() => setSelectedStoreVariant(null)}
          storeVariantId={selectedStoreVariant.id}
          isPreorder={isPreorder || false}
          storeId={storeInfo.id}
          variantName={selectedStoreVariant.product_variant.name}
          productId={selectedStoreVariant.product_variant.product.id}
        />
      )}
      {canManage && (
        <InventoryPublishDrawer
          storeInfo={storeInfo}
          isPreorder={isPreorder || false}
          isVisible={isInventoryDrawerOpen || false}
          onClose={() => {
            const baseUrl = isPreorder
              ? `/location/${slug}/inventory/pre-orders`
              : `/location/${slug}/inventory/same-day`
            navigate(baseUrl)
          }}
          activeTab={inventoryDrawerTab}
          onTabChange={(tab: string) => {
            const baseUrl = isPreorder
              ? `/location/${slug}/inventory/pre-orders/edit/${tab}`
              : `/location/${slug}/inventory/same-day/edit/${tab}`
            navigate(baseUrl)
          }}
          publishStoreVariants={
            isPreorder ? publishPreOrderStoreVariants : publishStoreVariants
          }
          unpublishStoreVariants={
            isPreorder ? unPublishPreOrderStoreVariants : unPublishStoreVariants
          }
          publishStoreModifiers={
            isPreorder ? publishPreOrderStoreModifiers : publishStoreModifiers
          }
          unpublishStoreModifiers={
            isPreorder
              ? unPublishPreOrderStoreModifiers
              : unPublishStoreModifiers
          }
          refetchStoreInventory={refetchStoreInventory}
          createStoreModifiers={createStoreModifiers}
          createStoreVariants={createStoreVariants}
          updateStoreCategoriesArrangements={updateStoreCategoriesArrangements}
          updateStoreModifierGroupArrangements={
            updateStoreModifierGroupArrangements
          }
        />
      )}
      {pendingAction && (
        <AvailabilityGroupModal
          visible={availabilityGroupModalVisible}
          onCancel={handleDismiss}
          onConfirm={handleConfirm}
          itemName={pendingAction.name}
          action={pendingAction.action}
        />
      )}
    </>
  )
}

export default Inventories
